import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useApp } from '@/context/AppContext';
import { ChevronRight, Loader2, Share2, AlertCircle } from 'lucide-react';
import { useKitData } from '@/hooks/useKitData';
import { useQuote } from '@/hooks/useQuote';
import { convertKitToBundleInfo } from '@/services/quoteService';
import KitSelectionCard from '@/components/KitSelectionCard';
import type { KitSelection } from '@/types/kit';

/**
 * ProductosStep Component - Kit-Based Selection
 * ============================================
 * 
 * This component implements the new kit-based product selection approach.
 * It replaces the previous individual product selection workflow.
 * 
 * KEY FEATURES:
 * - Displays complete CPAP kits (equipment + mask + accessories)
 * - Unified pricing and component management  
 * - Mask recommendation-based kit filtering
 * - Quote sharing functionality
 * - Auto-scroll to continue button after selection
 * 
 * ARCHITECTURE:
 * - Uses useKitData hook to fetch available kits
 * - KitSelectionCard component for kit display
 * - KitSelection type for selection state
 * - Integrates with quote service for sharing
 */


export default function ProductosStep() {
  const navigate = useNavigate();
  const { state, selectKit, getSelectedKit } = useApp();
  const { createQuote, shareQuoteUrl, isLoading: quoteLoading, error: quoteError } = useQuote();
  
  // Handle header back button clicks
  useEffect(() => {
    const handleHeaderBack = () => {
      navigate('/eleccion/felicitaciones');
    };

    window.addEventListener('headerBackClick', handleHeaderBack);
    return () => window.removeEventListener('headerBackClick', handleHeaderBack);
  }, [navigate]);

  const { maskRecommendation, maskSizing } = state.eleccion;
  const selectedKit = getSelectedKit();
  
  // Fetch kit data based on mask recommendation
  const { recommendedKits, isLoading, error } = useKitData(maskRecommendation);

  // Handle kit selection - auto-scroll to continue button after selection
  const handleKitSelection = (kitSelection: KitSelection) => {
    selectKit(kitSelection);
    
    // Auto-scroll to continue button after kit selection
    setTimeout(() => {
      const continueSection = document.getElementById('continue-section');
      if (continueSection) {
        const headerHeight = 100; // Height of the header
        const sectionTop = continueSection.offsetTop - headerHeight;
        window.scrollTo({
          top: sectionTop,
          behavior: 'smooth'
        });
      }
    }, 300);
  };

  const continuarAlPlan = async () => {
    if (!selectedKit) {
      return;
    }

    try {
      // Convert selected kit to BundleInfo for quote creation
      const bundleInfo = convertKitToBundleInfo(
        selectedKit,
        maskSizing?.maskSize
      );

      // Create quote with kit bundle information
      const quote = await createQuote({
        stage: 'carrito',
        bundleInfo
      });

      if (quote) {
        // Navigate to the quote URL
        navigate(`/${quote.id}`);
      }
    } catch (error) {
      console.error('Failed to create quote:', error);
    }
  };

  const handleShareQuote = async () => {
    if (!selectedKit) {
      return;
    }

    try {
      // Convert selected kit to BundleInfo for quote creation
      const bundleInfo = convertKitToBundleInfo(
        selectedKit,
        maskSizing?.maskSize
      );

      // Create quote with kit bundle information
      const quoteToShare = await createQuote({
        stage: 'productos',
        bundleInfo
      });

      if (quoteToShare) {
        await shareQuoteUrl();
      }
    } catch (error) {
      console.error('Failed to share quote:', error);
    }
  };

  const getKitsByBrand = () => {
    return recommendedKits;
  };
  
  const kitsToShow = getKitsByBrand();

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col min-h-[calc(100vh-80px)] justify-center items-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Cargando kits disponibles...</p>
        </div>
      </div>
    );
  }

  // Error state with fallback
  if (error || kitsToShow.length === 0) {
    return (
      <div className="flex flex-col min-h-[calc(100vh-80px)] justify-center items-center">
        <div className="flex flex-col items-center gap-4 text-center">
          <AlertCircle className="h-12 w-12 text-amber-600" />
          <div>
            <h3 className="text-lg font-semibold mb-2">Error al cargar kits</h3>
            <p className="text-muted-foreground">
              {error || 'No se encontraron kits disponibles para tu recomendación.'}
            </p>
          </div>
          <Button onClick={() => navigate('/eleccion/felicitaciones')} variant="outline">
            Volver
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-80px)] overflow-x-hidden">
      <div className="flex-1 container px-4 py-6 sm:py-8 pb-24 max-w-none">
        <div className="max-w-4xl mx-auto">

          {/* Kit Selection Section */}
          <div className="mb-8">
            <div className="text-center mb-4">
              <h2 className="text-2xl font-bold text-primary mb-2">Elige tu Kit</h2>
              <p className="text-muted-foreground px-4">
                Selecciona el kit que mejor se adapte a tus necesidades
              </p>
            </div>
            
            <div className="grid gap-6 sm:grid-cols-1 lg:grid-cols-2">
              {kitsToShow.map(kit => (
                <KitSelectionCard
                  key={kit.id}
                  kit={kit}
                  isSelected={selectedKit?.kit?.id === kit.id}
                  onSelect={handleKitSelection}
                  maskRecommendation={maskRecommendation}
                  maskSize={maskSizing?.maskSize}
                  className=""
                />
              ))}
            </div>
          </div>


          {/* Continue and Share buttons */}
          <div id="continue-section" className="space-y-3">
            <Button
              className="w-full min-h-touch text-lg px-6 sm:px-8 gap-0"
              onClick={continuarAlPlan}
              disabled={!selectedKit || quoteLoading}
            >
              {quoteLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  <span>Creando cotización...</span>
                </>
              ) : (
                <>
                  <span className="">
                    {!selectedKit
                      ? 'Selecciona un kit'
                      : 'Ver cotización'
                    }
                  </span>
                  {selectedKit && <ChevronRight style={{ marginLeft: '2.5px', width: '1.25rem', height: '1.25rem', strokeWidth: '3px'}}/>}
                </>
              )}
            </Button>
            
            {/* Show quote error if any */}
            {quoteError && (
              <p className="text-sm text-red-600 text-center">{quoteError}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}